/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { BeneficiaryAccount } from '~/types';
import { beneficiaries } from '~/routes';

import {
  createBeneficiaryAccount,
  deleteBeneficiaryAccount,
  editBeneficiaryAccount,
  getBeneficiaryAccounts,
} from '~/api/beneficiaryAccounts';

type UseMutationType = typeof useMutation;
type UseMutationParams = Parameters<UseMutationType>;

export const useBeneficiaryAccountsQuery = (
  beneficiaryId: string,
  options?: {
    onSuccess?: (res: BeneficiaryAccount[]) => void;
    enabled?: boolean;
  }
) => {
  const key = `${beneficiaries}/${beneficiaryId}`;
  return useQuery<BeneficiaryAccount[]>(
    key,
    () => getBeneficiaryAccounts(beneficiaryId, { page: 1, perPage: 9999 }).then((res) => res.data),
    options
  );
};

export const useBeneficiaryAccountsCreate = (options?: UseMutationParams[2]) => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();

  return useMutation((body: BeneficiaryAccount) => createBeneficiaryAccount(body), {
    onMutate(newData) {
      const key = `${beneficiaries}/${newData.beneficiaryId}`;
      if (options?.onMutate) options.onMutate(newData);

      const prevData = queryClient.getQueryData<BeneficiaryAccount[]>(key);

      const rollback = () => {
        queryClient.setQueryData(key, prevData);
      };

      queryClient.setQueryData<BeneficiaryAccount[] | undefined>(key, (prev) => {
        if (prev) {
          return prev.concat([newData]);
        }
        return prev;
      });

      void message.loading({ content: t('creating'), key });
      const successMessage = () => {
        void message.success({ content: t('created'), key, duration: 2 });
      };
      const errorMessage = (content: string) => {
        void message.error({ content, key, duration: 2 });
      };

      return {
        rollback,
        successMessage,
        errorMessage,
      };
    },
    onSuccess(res, newData, contenxt: any) {
      const key = `${beneficiaries}/${newData.beneficiaryId}`;

      if (options?.onSuccess) void options.onSuccess(res, newData, contenxt);
      contenxt.successMessage();
      void queryClient.invalidateQueries(key);
    },
    onError(error: any, newData, contenxt) {
      if (options?.onError) void options.onError(error, newData, contenxt);
      void contenxt.errorMessage(t(error?.response?.data?.message) || 'Error!');
      contenxt.rollback();
    },
  });
};

type DeleteBeneficiaryAccountRequest = {
  beneficiaryId: string;
  id: string;
};

export const useBeneficiaryAccountsDelete = (options?: UseMutationParams[2]) => {
  const queryClient = useQueryClient();
  const [t] = useTranslation();

  return useMutation(
    (request: DeleteBeneficiaryAccountRequest) =>
      deleteBeneficiaryAccount(request.beneficiaryId, request.id),
    {
      onMutate(request) {
        const key = `${beneficiaries}/${request.beneficiaryId}`;

        if (options?.onMutate) options.onMutate(request);
        const prevData = queryClient.getQueryData<BeneficiaryAccount[]>(key);
        const rollback = () => {
          queryClient.setQueryData(key, prevData);
        };

        queryClient.setQueryData<BeneficiaryAccount[] | undefined>(key, (prev) => {
          if (prev) {
            return prev.filter((item) => item.id !== request.id);
          }
          return prev;
        });

        void message.loading({ content: t('deleting'), key });
        const successMessage = () => {
          void message.success({ content: t('deleted'), key, duration: 2 });
        };
        const errorMessage = (content: string) => {
          void message.error({ content, key, duration: 2 });
        };

        return {
          rollback,
          successMessage,
          errorMessage,
        };
      },
      onSuccess(res, request, contenxt: any) {
        const key = `${beneficiaries}/${request.beneficiaryId}`;
        if (options?.onSuccess) void options.onSuccess(res, request, contenxt);
        contenxt.successMessage();
        void queryClient.invalidateQueries(key);
      },
      onError(error: any, request, contenxt) {
        if (options?.onError) void options.onError(error, request, contenxt);
        void contenxt.errorMessage(t(error?.response?.data?.message) || 'Error!');
        contenxt.rollback();
      },
    }
  );
};

export const useBeneficiaryAccountsEdit = () => {
  const queryClient = useQueryClient();
  return useMutation(editBeneficiaryAccount, {
    onSuccess(res) {
      const key = `${beneficiaries}/${res.beneficiaryId}`;
      queryClient.setQueryData<BeneficiaryAccount[] | undefined>(key, (prev) => {
        if (prev) {
          return prev.map((prevItem) => {
            if (prevItem.id === res.id) return res;
            return prevItem;
          });
        }
        return prev;
      });
    },
  });
};
